import { PlatformIcon } from '@/components/creator/PlatformIcon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContactPlatform } from '../contact-drawer-url-state';
import { PLATFORM_BRAND_COLOR, PLATFORM_LABEL } from '../profile/map-creator-profile-to-contact';
import SegmentedToggle, { type SegmentedToggleOption } from '../SegmentedToggle';

interface PlatformToggleProps {
  value: ContactPlatform;
  onChange: (platform: ContactPlatform) => void;
  /**
   * Platforms the contact actually has a profile on. A platform not in this list
   * is disabled so we never present another platform's live data as this one's —
   * e.g. an IG-only creator's TikTok segment is inert. Omitted → all enabled.
   */
  availablePlatforms?: ContactPlatform[];
}

const PLATFORMS: ContactPlatform[] = ['ig', 'tiktok'];

/** Key of each platform for the shared `PlatformIcon` glyph mapping. */
const ICON_PLATFORM: Record<ContactPlatform, string> = { ig: 'instagram', tiktok: 'tiktok' };

/**
 * Instagram / TikTok segmented control for the profile view: each segment is the
 * platform's logo, in its brand colour when selected and grey otherwise; the
 * platform name is its accessible name and tooltip. Thin wrapper over the shared
 * SegmentedToggle.
 */
const PlatformToggle: React.FC<PlatformToggleProps> = ({ value, onChange, availablePlatforms }) => {
  const { t } = useTranslation();

  const options = useMemo<SegmentedToggleOption<ContactPlatform>[]>(
    () =>
      PLATFORMS.map((platform) => ({
        value: platform,
        label: PLATFORM_LABEL[platform],
        icon: <PlatformIcon platform={ICON_PLATFORM[platform]} size={16} decorative />,
        activeColor: PLATFORM_BRAND_COLOR[platform],
        disabled: availablePlatforms ? !availablePlatforms.includes(platform) : false,
      })),
    [availablePlatforms]
  );

  return (
    <SegmentedToggle
      value={value}
      onChange={onChange}
      options={options}
      ariaLabel={t('contactDrawer.platformToggle.label')}
      segmentHeight="28px"
      segmentPx="10px"
      fontSize="xs"
      iconOnly
    />
  );
};

export default PlatformToggle;
